import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import AddBvn from "../../../components/AddBvn";
import ConfirmWithOtp from "../../../components/ConfirmWithOtp";
import SuccessModal from "../../../components/SuccessModal";
import CreatePin from "./CreatePin";
import Parse from "../../../api/ApiConfig";
import { verifyBvn } from "../../../screens/users/utils";
import {
	formatDOBFromBVN,
	getOtp,
	updateUserAndPersonalData,
	verifyOtp,
} from "../../../screens/dashboard/DashboardUtils";
import { getCurrentUserInfo } from "../../../utils/UserAuth";

const ActivateWallet = ({ activeTab, setActiveTab }) => {
	const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState({
		bvn: "",
		homeAddress: "",
		otp: "",
		newPin: "",
		confirmPin: "",
		oldPin: "",
	});
	const [bvnData, setBvnData] = useState(null);
	const [, setReqOtp] = useState(null);
	const [isVerifyOtp, setIsVerifyOtp] = useState(null);

	const currentAdmin = getCurrentUserInfo();

	const { bvn, homeAddress, otp, newPin, confirmPin, oldPin } = formData;

	// handle number fields
	const handleNumberChange = (e) => {
		if (isNaN(e.target.value)) return;
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	// handle text fields
	const handleChange = (e) => {
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	// verify Admin BVN
	const onVerifyBVN = async () => {
		if (!homeAddress.length) {
			toast.error("Home address cannot be empty.");
			return;
		}
		setLoading(true);
		try {
			setLoading(true);
			const data = await verifyBvn(bvn);
			setBvnData(data?.result);
			if (data) {
				setActiveTab(2);
			}
		} catch (error) {
			toast.error(error);
		} finally {
			setLoading(false);
		}
	};

	// admin request OTP to mail
	const onRequestOtp = async () => {
		setLoading(true);
		try {
			const response = await getOtp();
			if (response) {
				setReqOtp(response);

				toast.success(`OTP has been sent to your ${currentAdmin?.email}.`);
			}
		} catch (error) {
			toast.error("Failed to send OTP");
		} finally {
			setLoading(false);
		}
	};

	// otp verifiation
	const onVerifyOtp = async () => {
		setReqOtp(null);
		try {
			setLoading(true);
			const response = await verifyOtp(otp);
			if (response) {
				setIsVerifyOtp(response);

				toast.success("Your OTP verification is successful");
				setTimeout(() => {}, 2000);
			}
		} catch (error) {
			toast.error("Failed to send OTP");
		} finally {
			setLoading(false);
		}
	};

	// save details to respective classes
	const onVerifyCodeComplete = async () => {
		const User = Parse.Object.extend("_User");
		const user = new User();
		user.id = Parse.User.current()?.id;

		const data = {
			gender: bvnData?.data?.gender,
			userId: user,
			user: user,
			phone: currentAdmin?.phone,
			DOB: formatDOBFromBVN(bvnData?.data?.dob),
			fullName: `${currentAdmin?.firstName} ${currentAdmin?.lastName}`,
			BVN: bvnData?.data?.bvn,
			email: currentAdmin?.email,
			homeAddress,
		};

		const dataForUserProfile = {
			gender: bvnData?.data?.gender,
			DOB: formatDOBFromBVN(bvnData?.data?.dob),
			dateOfBirth: formatDOBFromBVN(bvnData?.data?.dob),
			bvn: bvnData?.data?.bvn,
			homeAddress,
		};

		try {
			setLoading(true);
			const response = await updateUserAndPersonalData(data, dataForUserProfile);
			console.log(response);

			if (response) {
				setActiveTab(3);
			}
		} catch (error) {
			toast.error(error);
		} finally {
			setLoading(false);
		}
	};

	const onProceedToCreatePin = () => {
		setActiveTab(4);
	};

	// admin creates pin
	const onCreatePin = async () => {
		if (currentAdmin?.accessPin && oldPin !== currentAdmin?.accessPin) {
			toast.error("Old pin is incorrect");
		}
		if (confirmPin !== newPin) {
			toast.error("New pin does not match");
			return;
		}
		try {
			setLoading(true);
			const query = new Parse.Query("User");
			const object = await query.get(Parse.User.current()?.id);

			await object.save({ accessPin: confirmPin });

			toast.success("Your transaction pin has been created successfully.");
			setTimeout(() => {
				window.location.reload();
			}, 1500);
		} catch (error) {
			console.log(error);
			if (error?.message === "Network Error") {
				toast.error("Oops, Service provider has been disconnected!");
			} else {
				toast.error("Failed to create transaction pin.");
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (isVerifyOtp) {
			setReqOtp(null);
			return;
		}
		if (otp.length < 6) {
			return;
		} else if (isNaN(otp)) {
			return;
		} else {
			onVerifyOtp();
		}
	}, [otp, isVerifyOtp]);

	useEffect(() => {
		if (bvnData) {
			setActiveTab(2);
		} else {
			return;
		}
	}, [bvnData]);

	return (
		<>
			{activeTab === 1 && (
				<AddBvn
					handleChange={handleNumberChange}
					bvn={bvn}
					homeAddress={homeAddress}
					onGreenBtnClick={onVerifyBVN}
					isLoading={loading}
					handleChangeTwo={handleChange}
				/>
			)}
			{activeTab === 2 && (
				<ConfirmWithOtp
					handleChange={handleNumberChange}
					otp={otp}
					onProceed={onVerifyCodeComplete}
					onGetOtpClick={onRequestOtp}
					isBtnDisabled={!isVerifyOtp}
					isLoading={loading}
				/>
			)}
			{activeTab === 3 && (
				<SuccessModal
					modalText='Your BVN has been saved successfully. Continue to create pin.'
					onReqClick={onProceedToCreatePin}
					btnText='proceed'
				/>
			)}
			{activeTab === 4 && (
				<CreatePin
					handleChange={handleNumberChange}
					newPin={newPin}
					confirmPin={confirmPin}
					oldPin={oldPin}
					onCreatePin={onCreatePin}
					isLoading={loading}
					accessPin={currentAdmin?.accessPin}
				/>
			)}
		</>
	);
};

ActivateWallet.propTypes = {
	activeTab: PropTypes.number,
	setActiveTab: PropTypes.func,
};

export default ActivateWallet;
